import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * <sos-signup> — página PÚBLICA de cadastro (B2C).
 *
 * O responsável abre o link da escola (?t=<token>), preenche os próprios dados e
 * os do(s) filho(s) e envia. Nada é criado na hora: vira uma solicitação que a
 * escola aprova no Painel. A marca (nome/logo/cor) e as vagas vêm da API pelo
 * token — o UUID da escola nunca aparece no navegador.
 *
 * CRUD enxuto por decisão de produto: nome, email, telefone. Sem CPF.
 */

interface SchoolYear {
  code: string;
  label: string;
}

interface SignupPage {
  school_name: string;
  logo_url: string | null;
  accent: string | null;
  portal_name: string | null;
  portal_logo_url: string | null;
  school_years: SchoolYear[];
  seats_free: number;
  accepting: boolean;
  /** A escola tem lista de alunos importada. Com lista, a série vem da turma no
   *  sistema acadêmico e o formulário não pergunta — perguntar convidaria o
   *  responsável a errar num dado que já temos correto. */
  tem_lista?: boolean;
}

interface ChildRow {
  name: string;
  email: string;
  anoletivo: string;
  /** Matrícula na escola. É o verificador FORTE: está no boleto e no portal, a
   *  família tem e um estranho normalmente não. Opcional — quem não tem em mãos
   *  não fica travado, só entra como verificação parcial. */
  matricula: string;
  /** Nascimento (YYYY-MM-DD). Sem matrícula, é o que permite localizar o aluno
   *  com segurança: nome de criança também não é segredo, e homônimos existem. */
  nascimento: string;
}

@customElement('sos-signup')
export class SosSignup extends LitElement {
  /** URL base da API SOS (ex.: https://api.sosalunos.app.br). */
  @property({ attribute: 'api-base' }) apiBase = '';
  /** Token do link da escola. Se vazio, lê de ?t= na própria URL. */
  @property() token = '';

  @state() private loading = true;
  @state() private page: SignupPage | null = null;
  @state() private loadError = '';

  @state() private guardianName = '';
  @state() private guardianEmail = '';
  @state() private guardianPhone = '';
  @state() private guardianIdade = '';
  // E-mail para onde o acesso vai — congelado no envio, para a tela de
  // sucesso mostrar exatamente o que a pessoa digitou.
  @state() private sentTo = '';
  @state() private consent = false;
  @state() private kids: ChildRow[] = [{ name: '', email: '', anoletivo: '', matricula: '', nascimento: '' }];
  // Honeypot: invisível pro humano; se vier preenchido é bot.
  @state() private website = '';

  @state() private sending = false;
  @state() private sendError = '';
  @state() private done = false;

  static styles = css`
    /* Identidade visual OlharSaber: azul-marinho nos títulos, laranja no CTA,
       fundo azul-claro, botões pill e Inter. O --accent segue sobreponível pelo
       tema da escola (white-label) — o laranja é só o padrão da marca.
       Inter NÃO é carregada do Google Fonts de propósito: a página é pública e
       um request a terceiro entregaria o IP do responsável (ponto de LGPD que já
       documentamos). Cai no system-ui, que é visualmente próximo. */
    :host {
      display: block;
      --accent: #ff6f43;
      --ink: #062e51;
      --muted: #6b7c8d;
      --page: #f2f9ff;
      --field: #f5f7fa;
      font-family: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
      color: var(--ink);
      line-height: 1.55;
      background: var(--page);
      min-height: 100vh;
    }
    .wrap {
      max-width: 600px;
      margin: 0 auto;
      padding: 40px 18px 64px;
    }
    .card {
      background: #fff;
      border: 1px solid #e6eef6;
      border-radius: 20px;
      padding: 34px 30px;
      box-shadow: 0 10px 40px rgba(6, 46, 81, 0.08);
    }
    /* Faixa da PLATAFORMA (rede) — separada do cartão, como o header do site. */
    .topbar {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 20px;
    }
    .topbar img {
      max-height: 44px;
      max-width: 190px;
      object-fit: contain;
    }
    .topbar .pf-name {
      font-size: 19px;
      font-weight: 700;
      color: var(--ink);
      letter-spacing: -0.3px;
    }
    /* Bloco da ESCOLA, dentro do cartão. */
    .brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      margin-bottom: 24px;
      padding-bottom: 20px;
      border-bottom: 1px solid #eef4f9;
      text-align: center;
    }
    .brand img {
      max-height: 58px;
      max-width: 190px;
      object-fit: contain;
      margin-bottom: 2px;
    }
    .brand .school {
      font-size: 19px;
      font-weight: 700;
      color: var(--ink);
      letter-spacing: -0.3px;
      line-height: 1.25;
    }
    /* Vínculo institucional: "esta escola faz parte da rede X". */
    .brand .tie {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 12.5px;
      font-weight: 600;
      color: var(--muted);
      background: var(--page);
      border: 1px solid #dbeafe;
      border-radius: 999px;
      padding: 5px 13px;
    }
    .brand .tie-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--accent);
      flex: 0 0 auto;
    }
    h1 {
      font-size: 25px;
      font-weight: 700;
      color: var(--ink);
      margin: 0 0 8px;
      letter-spacing: -0.5px;
      text-align: center;
    }
    .sub {
      color: var(--muted);
      font-size: 14.5px;
      margin: 0 0 26px;
      text-align: center;
    }
    .sec {
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: var(--accent);
      margin: 26px 0 12px;
    }
    label {
      display: block;
      font-size: 13.5px;
      font-weight: 600;
      margin: 0 0 6px;
      color: var(--ink);
    }
    .opt {
      font-weight: 400;
      color: #9bacbd;
    }
    /* Obrigatório precisa de marca PRÓPRIA. Só rotular os outros como
       "(opcional)" faz o pai deduzir pela ausência, e botão desabilitado não
       diz QUAL campo falta. */
    .req {
      color: var(--sos-accent, #ff6f43);
      font-weight: 700;
      margin-left: 2px;
    }
    .legenda {
      margin: -2px 0 14px;
      font-size: 12.5px;
      color: #6b7c89;
    }
    input[type='text'],
    input[type='email'],
    input[type='tel'],
    input[type='number'],
    input[type='date'],
    select {
      width: 100%;
      box-sizing: border-box;
      padding: 13px 16px;
      border: 1.5px solid transparent;
      border-radius: 12px;
      background: var(--field);
      font-size: 15px;
      font-family: inherit;
      color: var(--ink);
      outline: none;
      margin-bottom: 14px;
      transition: border-color 0.15s, background 0.15s;
    }
    input::placeholder {
      color: #a8b6c4;
    }
    input:focus,
    select:focus {
      background: #fff;
      border-color: var(--accent);
    }
    /* NAO usar appearance:none aqui: some com o botao de calendario do
       navegador, e o campo vira uma caixa de texto com mascara. Basta igualar a
       altura, que o date nativo traz padding proprio e fica mais baixo que o
       campo ao lado. */
    input[type='date'] {
      min-height: 48px;
    }
    /* Sem as setas de incremento: elas encolhem o campo e destoam dos demais. */
    input[type='number'] {
      -moz-appearance: textfield;
      appearance: textfield;
    }
    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* O calendario e ALTERNATIVA, nao substituto: quem prefere digitar usa os
       segmentos normalmente. Por isso nao ha handler abrindo o seletor no clique
       ou no foco — isso engolia a digitacao. So aumentamos o icone, que e o alvo
       de quem quer o calendario, especialmente no toque. */
    input[type='date']::-webkit-calendar-picker-indicator {
      cursor: pointer;
      opacity: 0.65;
      padding: 6px;
      margin-right: -2px;
      transform: scale(1.25);
    }
    input[type='date']:hover::-webkit-calendar-picker-indicator {
      opacity: 1;
    }
    .msg.envio {
      margin: 4px 0 14px;
      padding: 13px 16px;
      border-radius: 12px;
      border-left: 4px solid #b42318;
      text-align: left;
      font-size: 14px;
      line-height: 1.5;
    }
    .dupla {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    /* Num celular estreito os dois campos lado a lado ficam pequenos demais
       para digitar matrícula e escolher data. */
    @media (max-width: 420px) {
      .dupla {
        grid-template-columns: 1fr;
      }
    }
    .ajuda {
      margin: 6px 0 0;
      font-size: 12.5px;
      line-height: 1.45;
      color: #6b7c8d;
    }
    .kid {
      border: 1px solid #e6eef6;
      border-radius: 16px;
      padding: 18px 18px 4px;
      margin-bottom: 14px;
      background: #fbfdff;
    }
    .kid-hd {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }
    .kid-hd span {
      font-size: 13px;
      font-weight: 700;
      color: var(--ink);
    }
    .rm {
      background: none;
      border: 0;
      color: #b42318;
      cursor: pointer;
      font-size: 12.5px;
      font-family: inherit;
      padding: 2px 4px;
    }
    .addkid {
      background: #fff;
      border: 1.5px dashed #cfe0ee;
      border-radius: 999px;
      padding: 12px;
      width: 100%;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      font-family: inherit;
      color: var(--ink);
      margin-bottom: 6px;
      transition: border-color 0.15s, color 0.15s;
    }
    .addkid:hover:not(:disabled) {
      border-color: var(--accent);
      color: var(--accent);
    }
    .addkid:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .consent {
      display: flex;
      gap: 11px;
      align-items: flex-start;
      margin: 22px 0 4px;
      font-size: 13.5px;
      color: var(--muted);
      cursor: pointer;
    }
    .consent input {
      margin: 3px 0 0;
      width: 17px;
      height: 17px;
      flex: 0 0 auto;
      accent-color: var(--accent);
    }
    .submit {
      width: 100%;
      margin-top: 20px;
      padding: 15px 22px;
      background: var(--accent);
      color: #fff;
      border: 0;
      border-radius: 999px;
      font-size: 15.5px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      letter-spacing: 0.2px;
      box-shadow: 0 6px 18px rgba(6, 46, 81, 0.18);
      transition: filter 0.15s, box-shadow 0.15s;
    }
    .submit:hover:not(:disabled) {
      filter: brightness(1.06);
    }
    .submit:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
    }
    .msg {
      padding: 14px 16px;
      border-radius: 12px;
      font-size: 14px;
      margin-bottom: 16px;
    }
    .msg.err {
      background: #fef3f2;
      border: 1px solid #fda29b;
      color: #b42318;
    }
    .msg.warn {
      background: #fff8ed;
      border: 1px solid #fdca7a;
      color: #b54708;
    }
    .msg.info {
      background: var(--page);
      border: 1px solid #dbeafe;
      color: var(--ink);
    }
    .seats {
      font-size: 12.5px;
      color: var(--muted);
      margin-top: 4px;
      text-align: center;
    }
    .ok .dest {
      font-weight: 700;
      color: var(--accent, #ef6c33);
      font-size: 16px;
      word-break: break-all;
      margin: 4px 0 0;
    }
    .ok .dest-obs {
      font-size: 13px;
      color: #6b7c89;
      margin: 10px 0 0;
    }
    .ok {
      text-align: center;
      padding: 30px 8px 12px;
    }
    .ok .ico {
      font-size: 46px;
      line-height: 1;
      margin-bottom: 14px;
    }
    .ok h2 {
      font-size: 21px;
      font-weight: 700;
      color: var(--ink);
      margin: 0 0 10px;
    }
    .ok p {
      color: var(--muted);
      font-size: 15px;
      margin: 0;
    }
    .hp {
      position: absolute;
      left: -9999px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    }
    .foot {
      text-align: center;
      font-size: 12.5px;
      color: #9bacbd;
      margin-top: 20px;
    }
    .skel {
      text-align: center;
      padding: 48px 10px;
      color: var(--muted);
      font-size: 14.5px;
    }
    @media (max-width: 520px) {
      .wrap {
        padding: 22px 14px 48px;
      }
      .card {
        padding: 26px 20px;
        border-radius: 16px;
      }
      h1 {
        font-size: 22px;
      }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    if (!this.token) {
      this.token = new URLSearchParams(window.location.search).get('t') ?? '';
    }
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    if (!this.token) {
      this.loadError = 'Link inválido — peça um novo convite para a escola.';
      this.loading = false;
      return;
    }
    try {
      const r = await fetch(
        `${this.apiBase}/api/v1/public/signup/${encodeURIComponent(this.token)}`,
      );
      if (!r.ok) {
        this.loadError =
          r.status === 404
            ? 'Este link não é mais válido. Peça um novo convite para a escola.'
            : 'Não consegui carregar o formulário agora. Tente novamente em instantes.';
        return;
      }
      this.page = (await r.json()) as SignupPage;
      // A cor NÃO vem do tema da escola de propósito: o cadastro é uma superfície
      // da marca (OlharSaber), e o accent da escola descaracterizava a página
      // (ex.: Escola Duca pinta tudo de azul #378add). A escola entra com LOGO e
      // NOME; a paleta é a do produto. Pra voltar ao white-label aqui, basta
      // reaplicar `this.page.accent` em --accent.
    } catch {
      this.loadError = 'Sem conexão com o servidor. Verifique sua internet e tente de novo.';
    } finally {
      this.loading = false;
    }
  }

  private setKid(i: number, field: keyof ChildRow, value: string): void {
    const next = this.kids.map((c, idx) => (idx === i ? { ...c, [field]: value } : c));
    this.kids = next;
  }

  private addKid(): void {
    if (this.kids.length >= 10) return;
    this.kids = [...this.kids, { name: '', email: '', anoletivo: '', matricula: '', nascimento: '' }];
  }

  private rmKid(i: number): void {
    if (this.kids.length <= 1) return;
    this.kids = this.kids.filter((_, idx) => idx !== i);
  }

  /** Leva a mensagem de erro para dentro da tela depois de renderizada. No
   *  celular o teclado e a rolagem podem deixar até o botão fora de vista. */
  private mostrarErro(): void {
    void this.updateComplete.then(() => {
      this.renderRoot
        .querySelector('.msg.envio')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /** Idade do responsável: número inteiro plausível. Faixa larga porque
   *  responsável legal pode ser avô ou irmão mais velho. */
  private get idadeValida(): boolean {
    const n = Number(this.guardianIdade);
    return Number.isInteger(n) && n >= 16 && n <= 120;
  }

  private get valid(): boolean {
    const okGuardian =
      this.guardianName.trim().length >= 2 &&
      /\S+@\S+\.\S+/.test(this.guardianEmail.trim()) &&
      this.idadeValida;
    // Nascimento é exigido: é o que permite localizar o estudante na lista da
    // escola quando a família não tem a matrícula em mãos. Sem ele, a conferência
    // não tem como distinguir homônimos e todo cadastro cairia em "não
    // encontrado" — pedindo pouco, a escola aprova sabendo com quem fala.
    const okKids =
      this.kids.length > 0 &&
      this.kids.every(
        (c) => c.name.trim().length >= 2 && !!c.nascimento && !!c.matricula.trim(),
      );
    return okGuardian && okKids && this.consent;
  }

  private async submit(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.valid || this.sending) return;
    this.sending = true;
    this.sendError = '';
    try {
      const body = {
        guardian: {
          name: this.guardianName.trim(),
          email: this.guardianEmail.trim(),
          phone: this.guardianPhone.trim() || null,
          idade: Number(this.guardianIdade),
          consent: this.consent,
        },
        children: this.kids.map((c) => ({
          name: c.name.trim(),
          email: c.email.trim() || null,
          anoletivo: c.anoletivo || null,
          matricula: c.matricula.trim() || null,
          data_nascimento: c.nascimento || null,
        })),
        website: this.website || null,
      };
      const r = await fetch(
        `${this.apiBase}/api/v1/public/signup/${encodeURIComponent(this.token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (r.status === 201) {
        this.sentTo = this.guardianEmail.trim();
        this.done = true;
        return;
      }
      let detail = '';
      try {
        const j = await r.json();
        detail = typeof j?.detail === 'string' ? j.detail : '';
      } catch {
        /* resposta sem corpo JSON */
      }
      if (r.status === 429) {
        this.sendError = 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
      } else {
        this.sendError =
          detail || 'Não consegui enviar seu cadastro agora. Tente novamente em instantes.';
      }
      // Vaga pode ter acabado enquanto preenchia — reflete o estado novo.
      if (r.status === 409) void this.load();
      this.mostrarErro();
    } catch {
      this.sendError = 'Sem conexão com o servidor. Verifique sua internet e tente de novo.';
      this.mostrarErro();
    } finally {
      this.sending = false;
    }
  }

  render() {
    if (this.loading) {
      return html`<div class="wrap">
        <div class="card"><div class="skel">Carregando…</div></div>
      </div>`;
    }
    if (this.loadError) {
      return html`<div class="wrap">
        <div class="card">
          <div class="msg err">${this.loadError}</div>
        </div>
      </div>`;
    }
    const p = this.page;
    if (!p) return nothing;

    if (this.done) {
      return html`<div class="wrap">
        ${this.renderTopbar(p)}
        <div class="card">
          ${this.renderBrand(p)}
          <div class="ok">
            <div class="ico">✅</div>
            <h2>Cadastro enviado!</h2>
            <p>
              A ${p.school_name} vai revisar as informações. Assim que for aprovado,
              as instruções de acesso são enviadas para:
            </p>
            <p class="dest">${this.sentTo}</p>
            <p class="dest-obs">
              Confira sua caixa de entrada e o spam. Se o e-mail estiver errado,
              refaça o cadastro com o endereço correto.
            </p>
          </div>
        </div>
        <div class="foot">
          Você pode fechar esta página.${p.portal_name
            ? html`<br />Plataforma <strong>${p.portal_name}</strong> · Tutor IA`
            : nothing}
        </div>
      </div>`;
    }

    const cheia = !p.accepting;
    return html`<div class="wrap">
      ${this.renderTopbar(p)}
      <div class="card">
        ${this.renderBrand(p)}
        <h1>Cadastro do estudante</h1>
        <p class="sub">
          Sujeito a revisão e liberação de acesso pela escola.
        </p>
        <p class="legenda">
          <span class="req" aria-hidden="true">*</span> Campos obrigatórios
        </p>

        ${cheia
          ? html`<div class="msg warn">
              No momento não há vagas disponíveis para novos cadastros nesta escola. Entre em
              contato com a secretaria.
            </div>`
          : nothing}
        <form @submit=${this.submit}>
          <div class="sec">Responsável</div>
          <label>Nome completo<span class="req" aria-hidden="true">*</span></label>
          <input
            type="text"
            .value=${this.guardianName}
            ?disabled=${cheia}
            autocomplete="name"
            @input=${(e: Event) => (this.guardianName = (e.target as HTMLInputElement).value)}
          />
          <label>E-mail<span class="req" aria-hidden="true">*</span></label>
          <input
            type="email"
            .value=${this.guardianEmail}
            ?disabled=${cheia}
            autocomplete="email"
            @input=${(e: Event) => (this.guardianEmail = (e.target as HTMLInputElement).value)}
          />
          <div class="dupla">
            <div>
              <label
                >Sua idade<span class="req" aria-hidden="true">*</span></label
              >
              <input
                type="number"
                inputmode="numeric"
                min="16"
                max="120"
                placeholder="ex.: 38"
                .value=${this.guardianIdade}
                ?disabled=${cheia}
                @input=${(e: Event) =>
                  (this.guardianIdade = (e.target as HTMLInputElement).value)}
              />
            </div>
            <div>
              <label>Telefone <span class="opt">(opcional)</span></label>
              <input
                type="tel"
                .value=${this.guardianPhone}
                ?disabled=${cheia}
                autocomplete="tel"
                placeholder="(11) 90000-0000"
                @input=${(e: Event) =>
                  (this.guardianPhone = (e.target as HTMLInputElement).value)}
              />
            </div>
          </div>

          <div class="sec">Estudante${this.kids.length > 1 ? 's' : ''}</div>
          ${this.kids.map(
            (c, i) => html`<div class="kid">
              <div class="kid-hd">
                <span>${this.kids.length > 1 ? `Estudante ${i + 1}` : 'Dados do estudante'}</span>
                ${this.kids.length > 1
                  ? html`<button type="button" class="rm" @click=${() => this.rmKid(i)}>
                      remover
                    </button>`
                  : nothing}
              </div>
              <label>Nome completo<span class="req" aria-hidden="true">*</span></label>
              <input
                type="text"
                .value=${c.name}
                ?disabled=${cheia}
                @input=${(e: Event) =>
                  this.setKid(i, 'name', (e.target as HTMLInputElement).value)}
              />
              <label>E-mail <span class="opt">(opcional)</span></label>
              <input
                type="email"
                .value=${c.email}
                ?disabled=${cheia}
                @input=${(e: Event) =>
                  this.setKid(i, 'email', (e.target as HTMLInputElement).value)}
              />
              <div class="dupla">
                <div>
                  <label
                    >Matrícula<span class="req" aria-hidden="true">*</span>
                    <span class="opt">(está no boleto)</span></label
                  >
                  <input
                    type="text"
                    inputmode="numeric"
                    placeholder="ex.: 20250062"
                    .value=${c.matricula}
                    ?disabled=${cheia}
                    @input=${(e: Event) =>
                      this.setKid(i, 'matricula', (e.target as HTMLInputElement).value)}
                  />
                </div>
                <div>
                  <label
                    >Data de nascimento<span class="req" aria-hidden="true">*</span></label
                  >
                  <input
                    type="date"
                    min="2000-01-01"
                    max="2030-12-31"
                    .value=${c.nascimento}
                    ?disabled=${cheia}
                    @input=${(e: Event) =>
                      this.setKid(i, 'nascimento', (e.target as HTMLInputElement).value)}
                  />
                </div>
              </div>
              <p class="ajuda">
                A matrícula e a data de nascimento são conferidas com o cadastro
                da escola — é assim que confirmamos que você é responsável por
                este estudante.
              </p>
              ${p.school_years.length && !p.tem_lista
                ? html`<label>Ano/série <span class="opt">(opcional)</span></label>
                    <select
                      ?disabled=${cheia}
                      @change=${(e: Event) =>
                        this.setKid(i, 'anoletivo', (e.target as HTMLSelectElement).value)}
                    >
                      <option value="">Selecione…</option>
                      ${p.school_years.map(
                        (y) => html`<option
                          value=${y.label}
                          ?selected=${c.anoletivo === y.label}
                        >
                          ${y.label}
                        </option>`,
                      )}
                    </select>`
                : nothing}
            </div>`,
          )}
          <button
            type="button"
            class="addkid"
            ?disabled=${cheia || this.kids.length >= 10 || this.kids.length >= p.seats_free}
            @click=${this.addKid}
          >
            + Adicionar outro estudante
          </button>
          ${p.accepting
            ? html`<div class="seats">
                ${p.seats_free === 1
                  ? 'Resta 1 vaga nesta escola.'
                  : `Restam ${p.seats_free} vagas nesta escola.`}
              </div>`
            : nothing}

          <!-- honeypot: invisível pro humano -->
          <div class="hp" aria-hidden="true">
            <label>Não preencha este campo</label>
            <input
              type="text"
              tabindex="-1"
              autocomplete="off"
              .value=${this.website}
              @input=${(e: Event) => (this.website = (e.target as HTMLInputElement).value)}
            />
          </div>

          <label class="consent">
            <input
              type="checkbox"
              ?disabled=${cheia}
              .checked=${this.consent}
              @change=${(e: Event) => (this.consent = (e.target as HTMLInputElement).checked)}
            />
            <span>
              Autorizo o uso dos dados informados para criar e manter o acesso do(a) estudante
              à plataforma da escola.<span class="req" aria-hidden="true">*</span>
            </span>
          </label>

          <!-- O erro fica JUNTO do botão, não no topo: quem clica em Enviar está
               no fim da página, e uma mensagem lá em cima nunca é vista — a
               pessoa acha que nada aconteceu e clica de novo. -->
          ${this.sendError
            ? html`<div class="msg err envio" role="alert" aria-live="assertive">
                ${this.sendError}
              </div>`
            : nothing}
          <button class="submit" type="submit" ?disabled=${cheia || !this.valid || this.sending}>
            ${this.sending ? 'Enviando…' : 'Enviar cadastro'}
          </button>
        </form>
      </div>
      <div class="foot">
        Seus dados são usados apenas para o acesso escolar.${p.portal_name
          ? html`<br />Plataforma <strong>${p.portal_name}</strong> · Tutor IA`
          : nothing}
      </div>
    </div>`;
  }

  /** Faixa superior = marca da PLATAFORMA (a rede). Fica separada do bloco da
   *  escola pra o responsável entender de cara: "esta escola faz parte do X". */
  private renderTopbar(p: SignupPage) {
    // SÓ com logo: sem imagem, o nome solto em texto no topo ficava feio e
    // competia com o nome da escola. O vínculo com a rede continua sendo dito no
    // selo "Escola parceira X" dentro do cartão.
    if (!p.portal_logo_url) return nothing;
    return html`<div class="topbar">
      <img src=${p.portal_logo_url} alt=${p.portal_name ?? ''} />
    </div>`;
  }

  /** Bloco da ESCOLA dentro do cartão, com o vínculo institucional explícito. */
  private renderBrand(p: SignupPage) {
    return html`<div class="brand">
      ${p.logo_url
        ? html`<img src=${p.logo_url} alt=${p.school_name} />`
        : nothing}
      <div class="school">${p.school_name}</div>
      ${p.portal_name
        ? html`<div class="tie">
            <span class="tie-dot"></span>Escola parceira ${p.portal_name}
          </div>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sos-signup': SosSignup;
  }
}
